/**
 * Users API Client
 * Based on 01-api-reference.md User Management API
 */

import { getAgentHeaders } from './settings';

const AGENT_API_BASE_URL = '/api/v1';

// Types
export interface UserInfo {
  name?: string;
  birth_date?: string;
  gender?: string;
  [key: string]: unknown;
}

export interface DeviceInfo {
  os?: string;
  app_version?: string;
  [key: string]: unknown;
}

export interface TierInfo {
  id: string;
}

export interface UsageInfo {
  storage_bytes: number;
  document_count: number;
  memory_count: number;
  thread_count: number;
  extra?: Record<string, unknown>;
}

export interface User {
  user_id: string;
  created_at: string;
  updated_at: string;
  tier: TierInfo;
  user: UserInfo;
  device: DeviceInfo;
  custom: Record<string, unknown>;
  settings: Record<string, unknown>;
}

export interface UserDetail extends User {
  usage: UsageInfo;
  identity_profile?: Record<string, unknown>;
}

export interface UserListItem {
  user_id: string;
  tier: TierInfo;
  user: UserInfo;
  usage: UsageInfo;
  settings: Record<string, unknown>;
  is_playground: boolean;
  created_at: string;
  updated_at: string;
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface UserListResponse {
  users: UserListItem[];
  pagination: PaginationInfo;
}

export interface UserCreateRequest {
  user: UserInfo;
  device?: DeviceInfo;
  custom?: Record<string, unknown>;
  settings?: Record<string, unknown>;
}

export interface UserUpdateRequest {
  tier_id?: string;
  user?: UserInfo;
  device?: DeviceInfo;
  custom?: Record<string, unknown>;
  settings?: Record<string, unknown>;
}

export interface CloneUserResponse {
  user_id: string;
  counts: Record<string, number>;
}

export interface UserListParams {
  tier_id?: string;
  search?: string;
  source?: 'playground' | 'real';
  admin_user_id?: string;
  created_after?: string;
  created_before?: string;
  limit?: number;
  offset?: number;
  order_by?: string;
  order?: 'asc' | 'desc';
}

/**
 * Users API
 */
export const usersApi = {
  /**
   * Create a new user
   */
  async create(request: UserCreateRequest): Promise<User> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users`, {
      method: 'POST',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail?.detail || error.detail || 'Failed to create user');
    }

    return response.json();
  },

  /**
   * List users with filtering and pagination
   */
  async list(params: UserListParams = {}): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    if (params.tier_id) queryParams.append('tier_id', params.tier_id);
    if (params.search) queryParams.append('search', params.search);
    if (params.source) queryParams.append('source', params.source);
    if (params.admin_user_id) queryParams.append('admin_user_id', params.admin_user_id);
    if (params.created_after) queryParams.append('created_after', params.created_after);
    if (params.created_before) queryParams.append('created_before', params.created_before);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.order_by) queryParams.append('order_by', params.order_by);
    if (params.order) queryParams.append('order', params.order);

    const url = `${AGENT_API_BASE_URL}/users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetch(url, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list users: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get user detail by ID
   */
  async getById(userId: string): Promise<UserDetail> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}`, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found');
      }
      throw new Error(`Failed to get user: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Update user
   */
  async update(userId: string, data: UserUpdateRequest): Promise<User> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}`, {
      method: 'PATCH',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail?.detail || error.detail || 'Failed to update user');
    }

    return response.json();
  },

  /**
   * Delete user and all related data
   */
  async delete(userId: string): Promise<void> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}`, {
      method: 'DELETE',
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete user: ${response.statusText}`);
    }
  },

  /**
   * Clone a user (debug) — deep-copies everything except *_id (threads, messages,
   * memories, documents, LangGraph checkpoints + MinIO files). The clone is tagged
   * settings.source=playground + admin_user_ids=[adminUserId] so it appears in the
   * triggering admin's playground; created_by=clone is forced server-side.
   * Calls agent-server directly via /api/v1 (nginx 300s — power users take ~30s).
   */
  async clone(userId: string, adminUserId: string): Promise<CloneUserResponse> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}/clone`, {
      method: 'POST',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ source: 'playground', admin_user_ids: [adminUserId] }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail?.detail || error.detail || 'Failed to clone user');
    }

    return response.json();
  },
};

export default usersApi;
